// The generic rich_data envelope — transport for pure-data document kinds
// (contacts, contracts, …). The JSON in data-doc is the only source of truth;
// the wrapper's inner markup is a machine-generated plain-text digest so
// TEXT_SHORT, email rendering, and kind-unaware surfaces degrade to readable
// text instead of raw JSON. Nothing ever parses the digest back.
//
// The wrapper is a `<div desk_el>` subtree, which the real save sanitizer
// (msgdataApi clearHtml) leaves completely untouched — the envelope survives
// saves byte-identically, so the cheap canonical-prefix detection below stays
// valid for stored documents.

import { getAttr, parseHtml, type HtmlElement, type HtmlNode } from '@/msgdataApi/primitives/htmlDom'
import { escapeHtml } from './richElements'
import { valueRepresentations } from './valueRepresentations'

export interface RichDataEnvelope<TData = unknown> {
  schemaVersion: number
  kind: string
  data: TData
}

/** Detection allowlist: only these kinds resolve to their own content
 * document. A stored envelope with an unknown kind rides the html path and
 * renders its digest. */
export const KNOWN_DATA_KINDS = ['contacts', 'custom-html'] as const
export type KnownDataKind = (typeof KNOWN_DATA_KINDS)[number]

const DATA_KIND_RE = /^[a-z][a-z0-9_-]{0,40}$/
const DIGEST_LINE_MAX = 300

export function serializeDataDocument(envelope: RichDataEnvelope, digestLines: string[]): string {
  if (!DATA_KIND_RE.test(envelope.kind)) throw new Error(`invalid rich_data kind: ${envelope.kind}`)
  const doc = valueRepresentations.packText(JSON.stringify(envelope))
  const digest = digestLines
    .map(line => line.trim().slice(0, DIGEST_LINE_MAX))
    .filter(Boolean)
    .map(line => `<p>${escapeHtml(line)}</p>`)
    .join('')
  return `<div desk_el="rich_data" data-rich-version="1" data-kind="${envelope.kind}" data-doc="${doc}">${digest}</div>`
}

function findEnvelopeWrapper(nodes: HtmlNode[]): HtmlElement | null {
  // Whole-document contract: the first element node must be the envelope.
  for (const node of nodes) {
    if (node.kind === 'text') {
      if (node.value.trim()) return null
      continue
    }
    if (node.kind !== 'element') continue
    return node.name === 'div' && getAttr(node, 'desk_el') === 'rich_data' ? node : null
  }
  return null
}

export function parseDataDocument(html: string): RichDataEnvelope | null {
  const wrapper = findEnvelopeWrapper(parseHtml(html).body)
  if (!wrapper) return null
  const encoded = getAttr(wrapper, 'data-doc')
  if (!encoded) return null
  try {
    const parsed = JSON.parse(valueRepresentations.unpackText(encoded)) as RichDataEnvelope
    if (!parsed || typeof parsed !== 'object') return null
    if (typeof parsed.kind !== 'string' || !DATA_KIND_RE.test(parsed.kind)) return null
    if (typeof parsed.schemaVersion !== 'number') return null
    return parsed
  } catch {
    return null
  }
}

// Cheap detection for the content-document resolver: the serializer above is
// the only writer and clearHtml preserves desk_el subtrees byte-identically,
// so a canonical-prefix test is stable for stored documents.
const DATA_DOCUMENT_PREFIX_RE = /^<div desk_el="rich_data" data-rich-version="\d+" data-kind="([a-z][a-z0-9_-]{0,40})"/

export function detectDataDocumentKind(value: string): KnownDataKind | null {
  const match = DATA_DOCUMENT_PREFIX_RE.exec(value.trimStart())
  if (!match) return null
  const kind = match[1]
  return (KNOWN_DATA_KINDS as readonly string[]).includes(kind) ? (kind as KnownDataKind) : null
}
